/**
 * B57 — Apple Health import consent, PER ACCOUNT.
 *
 * ⚖️ FOUNDER RULING (B46 audit item C, ruled 2026-08-29): the HealthKit import
 * opt-in is CONSENT TO READ HEALTH DATA. Consent belongs to an account, never
 * to a piece of glass.
 *
 * The bug this replaces: `tayro.hkImport.enabled.v1` was a device-global
 * boolean. User A opting in left the import **armed** for user B on the same
 * device — B's Apple Watch workouts started flowing into B's Tayro account
 * without B ever being asked. Same "unstamped device blob adopted by the next
 * session" mechanism B46 fixed for the landed chip, on the one flag in the app
 * where the blob IS the consent.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE
 *
 *   consent is stored at `tayro.hkImport.enabled.v2:<userId>`
 *   a read for user X consults X's key and NOTHING else
 *   no user signed in  →  off, always, and nothing to write to
 *
 * Off stays off for everyone who has not opted in themselves. There is no
 * inheritance path between two accounts, in either direction.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE ONE-TIME LEGACY MIGRATION (and why it adopts rather than drops)
 *
 * A device upgrading into this build may carry a legacy device-global `'1'`.
 * Someone really did tap that toggle and really did mean it — dropping it
 * silently un-consents a real athlete and quietly stops importing their runs,
 * which reads as "the app broke". So the legacy value ADOPTS, ONCE, to the
 * user who is signed in at the first read after upgrade. On the founder's
 * family's devices that is overwhelmingly the person who tapped it: phones are
 * not shared.
 *
 * The adoption is bounded hard, in three ways:
 *
 *   1. ONE reader, ever. The legacy key is CONSUMED on the first read that
 *      observes it — removed whether or not it was adopted. A second account
 *      arriving later finds nothing to inherit, so the founder's bug cannot
 *      recur through the migration itself.
 *   2. NO SESSION → NO ADOPTION. If nobody is signed in at that first read,
 *      the legacy key is dropped unadopted. An unattributable consent must
 *      never transfer to a future stranger — the person who signs in next is
 *      exactly who this whole lane exists to protect.
 *   3. NEVER OVER AN ANSWER. If the reader already has a v2 entry of their
 *      own, that entry wins; the legacy value is discarded. A stale device
 *      blob cannot resurrect a consent someone has since turned off.
 *
 * ⚠️ This adopt-the-unclaimed rule is a ONE-TIME MIGRATION of a value written
 * by a build that predates per-account keys. It is NOT a general rule about
 * unstamped state, and it must not be generalised into one: extending it to
 * ongoing writes would rebuild the exact hole the founder ruled on. Contrast
 * B26 (`hooks/onboardingReducer.ts`), whose "unclaimed is not foreign" rule
 * for the wizard blob is permanent and correct for its own reason — that blob
 * is pre-auth work typed by the person about to sign in. Consent is not work
 * product; it does not get rescued.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE IS REACT-FREE
 *
 * `workoutImport.ts` re-exports `isImportEnabled` / `setImportEnabled` so its
 * export surface is unchanged for its readers. Keeping the store free of React
 * and of AuthContext means that re-export stays a pure-core import. The React
 * binding lives one file over in `useImportConsent.ts`.
 */

/** Legacy DEVICE-GLOBAL key. Read exactly once per device, then removed. */
export const LEGACY_IMPORT_ENABLED_KEY = 'tayro.hkImport.enabled.v1';

const IMPORT_ENABLED_PREFIX = 'tayro.hkImport.enabled.v2:';

/**
 * Per-account consent key. House convention — the same shape as
 * `normsAckStorageKey(coachUserId)` (`lib/map/raceLiveMapView.ts`) and the
 * user-keyed chat blobs in `lib/conversations.ts`: the key carries the owner,
 * and the caller supplies the id it is asking about.
 */
export function importConsentStorageKey(userId: string): string {
  return `${IMPORT_ENABLED_PREFIX}${userId}`;
}

/** What the one-time legacy migration did, for tests and for the report. */
export type LegacyAdoption =
  /** A legacy `'1'` became this user's own consent. */
  | 'adopted'
  /** A legacy value was consumed WITHOUT transferring (no session, legacy
   *  'off', junk, or the reader already had an answer of their own). */
  | 'dropped'
  /** No legacy key present — the ordinary steady state. */
  | 'none';

/**
 * Consume the legacy device-global flag, adopting it to `userId` when — and
 * only when — every bound in the header holds. Idempotent by construction:
 * the key is gone after the first call that sees it.
 *
 * Exported for the tests and for anyone auditing the migration; the read path
 * calls it, so no caller has to remember to.
 */
export function adoptLegacyImportConsent(userId: string | null): LegacyAdoption {
  let legacy: string | null;
  try {
    legacy = localStorage.getItem(LEGACY_IMPORT_ENABLED_KEY);
  } catch {
    return 'none'; // private mode / storage disabled — nothing to migrate
  }
  if (legacy === null) return 'none';

  // Bound ①: consumed on this read, adopted or not. Do this BEFORE deciding,
  // so an exception on the adopting write below can never leave the
  // device-global value behind for the next account to inherit.
  let consumed = true;
  try {
    localStorage.removeItem(LEGACY_IMPORT_ENABLED_KEY);
  } catch {
    consumed = false;
  }
  if (!consumed) {
    // Removal refused (storage hostile to deletes). Neutralise the value in
    // place instead — anything that is not '1' can never be adopted, so a
    // tombstone fences the next account exactly as a deletion would. If this
    // write fails too, storage is inert and nothing persists to be inherited.
    try {
      localStorage.setItem(LEGACY_IMPORT_ENABLED_KEY, '0');
    } catch {
      /* storage fully unavailable — nothing is durable on this device */
    }
  }

  // Bound ②: an unattributable consent is not transferable.
  if (!userId) return 'dropped';
  // The legacy writer only ever stored '1' for on and removed the key for off;
  // anything else is not an opt-in.
  if (legacy !== '1') return 'dropped';

  try {
    const key = importConsentStorageKey(userId);
    // Bound ③: this user's own answer, including an explicit off, wins.
    if (localStorage.getItem(key) !== null) return 'dropped';
    localStorage.setItem(key, '1');
    return 'adopted';
  } catch {
    return 'dropped';
  }
}

/**
 * Has THIS user opted into the Apple Health import on this device?
 *
 * Runs the one-time legacy migration first, so the answer is correct on the
 * very first read after upgrade without any caller doing migration bookkeeping.
 *
 * @param userId the signed-in account, or `null` for "auth has SETTLED and
 *   nobody is signed in". ⚠️ Do NOT pass `null` while auth is still resolving:
 *   a `null` read consumes the legacy key unadopted (bound ②), which would
 *   un-consent the very user who is about to appear. `useImportConsent` holds
 *   that gate for React callers.
 */
export function isImportEnabled(userId: string | null): boolean {
  adoptLegacyImportConsent(userId);
  if (!userId) return false;
  try {
    return localStorage.getItem(importConsentStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

/**
 * Record (or withdraw) THIS user's opt-in. A no-op with no session — there is
 * no account to attribute the consent to, and a device-scoped fallback is the
 * bug.
 */
export function setImportEnabled(userId: string | null, on: boolean): void {
  if (!userId) return;
  try {
    const key = importConsentStorageKey(userId);
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
    // An explicit answer settles the question for this device: retire the
    // legacy key so a later account can never adopt it.
    localStorage.removeItem(LEGACY_IMPORT_ENABLED_KEY);
  } catch {
    /* private mode / storage disabled — best effort */
  }
}
